<script setup lang="ts">
import { onErrorCaptured, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import ErrorFallback from '@/components/ErrorFallback.vue'
import NewVersionModal from '@/components/NewVersionModal.vue'
import MusicAuthDialog from '@/components/MusicAuthDialog.vue'
import PrivacyDialog from '@/components/PrivacyDialog.vue'
import { useSafeAreaInjection, useBackGestureReporter, useOfflineGuard } from '@/composables/useAppShell'
import { initPushCheck } from '@/composables/usePushCheck'
import { useVersionCheck } from '@/composables/useVersionCheck'
import { useImNotify } from '@/composables/useImNotify'
import ImToast from '@/components/ImToast.vue'
import LoadingProgress from '@/components/LoadingProgress.vue'
import { useRouteLoading } from '@/composables/useRouteLoading'

defineOptions({ name: 'AppRoot' })

const route = useRoute()
const { visible: routeLoadingVisible, percent: routeLoadingPercent } = useRouteLoading()

// 壳层关注点抽到 composables，App.vue 保持薄组合层
useSafeAreaInjection()
useBackGestureReporter()
const { toastState: imToastState, onClick: onClickImToast, hideToast: hideImToast } = useImNotify()
const { isOffline } = useOfflineGuard()
// 应用级版本检测：发现新版本时弹出全局更新弹窗
const { visible: versionVisible, versionData, close: closeVersionModal } = useVersionCheck()
// 应用级推送检查：登录态就绪即查询设备开启推送，失败指数退避重试
initPushCheck()

// 错误边界（onErrorCaptured 仅捕获子树渲染/生命周期错误；async/事件错误由 app.config.errorHandler 兜底）
const error = shallowRef<unknown>(null)
const errorKey = shallowRef(0)
onErrorCaptured((err) => {
  error.value = err
  return false
})
watch(() => route.fullPath, () => {
  error.value = null
  errorKey.value++
})
function resetError() {
  error.value = null
}

// KeepAlive 缓存清单（显式指定；页面须 defineOptions({ name })；:max 兜底防无限增长）
// TabHome 壳：进入子页再返回时保留 tab 状态（visitedTabs 等）
// ContentMusicPage：从专辑详情返回时保留虚拟列表与滚动位置
// ContentFavoritesPage：返回收藏列表时刷新概览（onActivated，等价 React useRefreshOnActivate）
// ContentSearchPage：保持缓存；仅从专辑详情返回时保留结果/滚动，从首页重新进入则清空（页内 onBeforeRouteLeave + onActivated 判定）
// PointsShopPage / PointsDetailPage：跳转商品/订单详情后返回保留数据与滚动位置
const cachedNames = ['TabHomePage', 'ContentMusicPage', 'ContentFavoritesPage', 'ContentSearchPage', 'PointsShopPage', 'PointsDetailPage']
</script>

<template>
  <LoadingProgress :visible="routeLoadingVisible" :percent="routeLoadingPercent" />
  <ErrorFallback v-if="error" :error="error" :key="errorKey" @reset="resetError" />
  <div v-else-if="isOffline" class="flex flex-col w-screen h-screen">
    <div class="pt-inset-top px-4 h-12 flex items-center text-ink">网络异常</div>
    <div class="flex flex-1 items-center justify-center text-muted text-sm">网络状态异常，请检查网络后重试</div>
    <div class="pb-inset-bottom" />
  </div>
  <RouterView v-else v-slot="{ Component }">
    <KeepAlive :include="cachedNames" :max="10">
      <component :is="Component" />
    </KeepAlive>
  </RouterView>
  <!-- 全局版本更新弹窗（Web 页面由 composable 内部跳过） -->
  <NewVersionModal :visible="versionVisible" :version-data="versionData" @close="closeVersionModal" />
  <!-- 网易云未绑定（接口 code 2901）统一授权弹窗，由 useMusicAuthDialog 全局单例驱动 -->
  <MusicAuthDialog />
  <!-- 用户协议/隐私政策更新弹窗，启动时查询未同意则弹 -->
  <PrivacyDialog />
  <ImToast :visible="imToastState.visible" :sender-name="imToastState.senderName" @click="onClickImToast"
    @close="hideImToast" />
</template>
